import {useHistoryAssetsByIdQuery} from '@domain/assets'
import type {ScheduleItem} from '@domain/schedule'
import type {Env} from '@hconnect/common/hproduce'
import {settings} from '@hconnect/common/hproduce/config/settings'
import {PLANT_ID} from '@hconnect/common/hproduce/consts'
import {roundTo15MinIntervalStart} from '@hconnect/common/utils'
import {dataTestId} from '@hconnect/uikit'
import {Alert, HPButton} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {Stack, Typography} from '@mui/material'
import {useCurrentTime, usePlanningChartStartEnd} from '@widgets/planning-schedule-chart'
import moment from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../consts'

const env = process.env.REACT_APP_STAGE as Env
const settingsBaseUrl = settings.url[env]

export const ScheduleItemStandardOperationTimeInfo = ({
  scheduleItem
}: {
  scheduleItem: ScheduleItem
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {plantId: plantCode} = useUpmPlantContext()
  const {startOfPlan, endOfPlan} = usePlanningChartStartEnd()

  const timezoneId = useUpmPlantTimezone()
  const now = useCurrentTime({timezoneId})

  const shouldShowOpenSettingsButton = moment
    .utc(scheduleItem.end)
    .isAfter(roundTo15MinIntervalStart(now))

  const {data: assetsById} = useHistoryAssetsByIdQuery({
    plantId: plantCode,
    timeFrame: [startOfPlan, endOfPlan]
  })

  if (!assetsById) {
    throw new Error('BUG: assetsById should be loaded to render this component')
  }

  const assetType = assetsById[scheduleItem.assetId].type
  const assetId = scheduleItem.assetId

  const handleOpenSettingsClick = () => {
    window.location.replace(
      `${settingsBaseUrl.replace(PLANT_ID, plantCode)}/assets/${assetType}/id/${assetId}`
    )
  }

  return (
    <Alert
      {...dataTestId('schedule_item_standard_operation_time_info')}
      content={
        <Stack
          direction="row"
          spacing={2}
          sx={{justifyContent: 'space-between', alignItems: 'center'}}
        >
          <Typography variant="body1">{t('planning.recurringItemInfo')}</Typography>
          {shouldShowOpenSettingsButton && (
            <HPButton
              variant="text"
              color="primary"
              onClick={handleOpenSettingsClick}
              {...dataTestId('schedule_item_asset_link_button')}
            >
              {t('common.openSettings')}
            </HPButton>
          )}
        </Stack>
      }
    />
  )
}
